import i18n from 'i18next';
import { DataJournal } from 'store/vat/models/vatReportData';
import { ExportHeader } from '../types/transaction.types';
import moment from 'moment';
import { NOT_CLICKED_ATTACHMENT } from '..';
import {
  formatNumberWithLocale,
  getDefaultLocale,
} from 'utils/numberFormat';

/** Matches AttachmentViewerWrapper `--av-viewer-width` default (standalone). */
const ATTACHMENT_VIEWER_WIDTH_PX = 670;
/** Gap between the transactions card and the fixed attachment panel. */
const ATTACHMENT_GAP_PX = 20;
/** VAT boxes panel width. Matches `.menu-wrapper` in the report layout. */
const VAT_BOXES_PANEL_PX = 360;
/** Gap between the transactions column and the boxes panel. */
const VAT_BOXES_GAP_PX = 40;
/** Right padding of `.content-wrapper`, outside the transactions column. */
const CONTENT_PADDING_RIGHT_PX = 40;

export const getHeaderForExport = (
  dataToExport: DataJournal[]
): ExportHeader[] => {
  const obj = dataToExport?.length > 0 ? dataToExport[0] : null;
  if (!obj) return [];

  return Object.keys(obj).map((key) => ({
    label: key.replace(/([a-z0-9])([A-Z])/g, '$1 $2'),
    key: key,
  }));
};

/** API groupingType query param for VAT transaction pages. */
export const journalTypeToGroupingType = (journalType?: string): string =>
  journalType?.toUpperCase() === 'SUMMARISED' ? 'summarised' : 'detailed';

export const filterTransactions = (
  transactions: DataJournal[],
  searchValue: string
): DataJournal[] => {
  if (!searchValue) return transactions;

  return (
    transactions?.filter((transaction) =>
      Object.keys(transaction).some((key) =>
        String(transaction[key])
          .toLowerCase()
          .includes(searchValue.toLowerCase())
      )
    ) || []
  );
};

export const formatDate = (date: string): string => {
  return moment(date).format('DD/MM/YYYY');
};

export const getTableWidth = (
  showSideMenu: boolean,
  _menuSize: string,
  attachmentsTransactionId: string,
  loading: boolean
): string => {
  const attachmentOpen =
    attachmentsTransactionId !== NOT_CLICKED_ATTACHMENT && !loading;
  const attachmentReserve = attachmentOpen
    ? ATTACHMENT_VIEWER_WIDTH_PX + ATTACHMENT_GAP_PX
    : 0;

  // The transactions column is the space beside the boxes panel, so the card
  // fills that column. A viewport calc was wider than the column and squeezed
  // the panel until "Upgrade" ran off its right edge.
  if (!attachmentOpen) {
    return '100%';
  }

  if (!showSideMenu) {
    return `calc(100% - ${attachmentReserve}px)`;
  }

  // The viewer is fixed to the viewport's right edge, so the panel, the row
  // gap, and the content padding already cover part of its width.
  const attachmentOverlap = Math.max(
    0,
    ATTACHMENT_VIEWER_WIDTH_PX -
      VAT_BOXES_PANEL_PX -
      VAT_BOXES_GAP_PX -
      CONTENT_PADDING_RIGHT_PX
  );
  return `calc(100% - ${attachmentOverlap}px)`;
};

/** Locale-aware amount formatting. Pass locale from useNumberFormat() when in a component. */
export const formatAmount = (
  amount: number | null | undefined,
  locale?: string
): string => {
  return formatNumberWithLocale(locale ?? getDefaultLocale(), amount, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export const getExceptionBackgroundColor = (
  exceptionActionType?: number
): string => {
  if (!exceptionActionType) return 'transparent';

  switch (exceptionActionType) {
    case 1: // ADJUSTMENT
      return '#bbbb42';
    case 2: // CURRENT_RETURN
      return '#48B9BF';
    default:
      return 'var(--color-surface, #f3f7f8)';
  }
};

export const getExceptionTooltipText = (
  exceptionActionType?: number
): string => {
  if (!exceptionActionType) return '';

  switch (exceptionActionType) {
    case 1: // ADJUSTMENT
      return i18n.t('vat-report.adjusted-exception-from-another-submission');
    case 2: // CURRENT_RETURN
      return i18n.t('vat-report.included-from-exceptions');
    default:
      return '';
  }
};
